import type { ReactNode } from 'react';
import { pipe, Option, Array as A } from 'effect';

import { BBAN_BRANCH_CODE_LENGTH, BBAN_BRANCH_CODE_REGEX } from '@/lib/bban';

// Branch-field logic shared by the account and beneficiary dialogs (DPM-901).
// The IBAN zero-pads the branch (@/lib/bban), so '22' and '022' are the same
// branch: codes are compared and manual ones stored padded.

type BranchOption = { label: string; value: string };

export type AddBranchOption = {
  label: string;
  value: string;
  isNew: true;
  startIcon?: ReactNode;
  dividerBefore?: boolean;
};

// A valid 1–3 digit code → zero-padded; anything else → trimmed as is.
export const normalizeBranchCode = (raw: string): string =>
  pipe(
    Option.some(raw.trim()),
    Option.filter((code) => BBAN_BRANCH_CODE_REGEX.test(code)),
    Option.map((code) => code.padStart(BBAN_BRANCH_CODE_LENGTH, '0')),
    Option.getOrElse(() => raw.trim()),
  );

export const isBranchCode = (raw: string): boolean => BBAN_BRANCH_CODE_REGEX.test(raw.trim());

export const findBranchOption = <T extends BranchOption>(options: readonly T[], raw: string): Option.Option<T> =>
  pipe(
    Option.some(normalizeBranchCode(raw)),
    Option.filter((code) => code.length > 0),
    Option.flatMap((code) => A.findFirst(options, (option) => normalizeBranchCode(option.value) === code)),
  );

export const isManualBranch = <T extends BranchOption>(options: readonly T[], value: string): boolean =>
  value.length > 0 && Option.isNone(findBranchOption(options, value));

// "+ Add" is offered for a valid code that no listed branch has.
export const shouldOfferAdd = <T extends BranchOption>(options: readonly T[], inputValue: string): boolean =>
  isBranchCode(inputValue) && Option.isNone(findBranchOption(options, inputValue));

// A manual value stays an option, so MUI can display it and retyping matches it.
export const withManualBranch = <T extends BranchOption>(
  options: readonly T[],
  value: string,
): Array<T | BranchOption> => (isManualBranch(options, value) ? [...options, { label: value, value }] : [...options]);

// A listed branch with the typed code goes first; otherwise "+ Add" goes last, after a divider.
export const buildBranchOptions = <T extends BranchOption>({
  filtered,
  options,
  inputValue,
  makeAddOption,
}: {
  filtered: readonly T[];
  options: readonly T[];
  inputValue: string;
  makeAddOption: (code: string) => AddBranchOption;
}): Array<T | AddBranchOption> =>
  shouldOfferAdd(options, inputValue)
    ? [...filtered, { ...makeAddOption(normalizeBranchCode(inputValue)), dividerBefore: filtered.length > 0 }]
    : pipe(
        isBranchCode(inputValue) ? findBranchOption(options, inputValue) : Option.none<T>(),
        Option.match({
          onNone: (): Array<T | AddBranchOption> => [...filtered],
          onSome: (match): Array<T | AddBranchOption> => [
            match,
            ...A.filter(filtered, (option) => option.value !== match.value),
          ],
        }),
      );
